import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { fetchGitConnection } from '@/services/git/api';
import type { GitConnection } from '@/services/git/types';
import { useAuthStoreV1 } from '@/stores/authStore_v1';

/**
 * Reads the connection. It does NOT create one.
 *
 * It used to adopt the gateway's GitHub token whenever the read came back empty, so
 * that a user who authorized under MCP arrived here already connected. That made
 * "Disconnect Git" impossible: DELETE removed the row, the next read found none and
 * re-adopted it, and the section was connected again inside a second. (Not noticed
 * at the time because the adoption POST was 401ing for an unrelated reason — it only
 * started undoing the disconnect once that was fixed.)
 *
 * Adoption is now what it always should have been: something the user asks for, in
 * `useAdoptGithubConnection`, wired to the Connect button.
 */
export function useGitConnection(): UseQueryResult<GitConnection | null, Error> {
  const token = useAuthStoreV1((s) => s.token);
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id);

  return useQuery({
    queryKey: ['gitConnection', orgId],
    queryFn: () => fetchGitConnection(orgId!),
    enabled: !!token && !!orgId,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}
